import React from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { Form } from 'react-final-form';
// import { toJS } from 'mobx';

import SvgIcon from 'common/components/SvgIcon';
import EngageTextInputField from '../../../../common/components/form-elements/textInputField/TextInputField';
import EngageTextareaInputField from '../../../../common/components/form-elements/textareaInputField/TextareaInputField';
import CheckboxInputField from '../../../../common/components/form-elements/checkboxInputField/CheckboxInputField';
import SwitchableButtonBar from '../../../../common/components/switchableButtonBar/SwitchableButtonBar';
import { getFormFieldClass, getFormFieldTranslationTooltip } from '../../../../common/TranslationUtils';
import { emailValidator } from '../../../../common/formValidators';

const initialValues = {
  isMailingActive: false,
  emailsToNotify: '',
  thankYouMessage: "Thank you for submitting your thoughts in Give My View's feedback section.",
  noReplyMessage:
    'This email cannot be replied to but be assured your comment has been recorded and has been passed to the project team.',
};

function MailingFeedbackConfiguration(props) {
  const { ProjectControlStore, ProjectStore } = props;

  const project = ProjectStore.project;
  const mailingConfiguration = project?.runtimeOptions?.feedbackMailingConfiguration || initialValues;
  // console.log('MailingFeedbackConfiguration', toJS(project?.runtimeOptions));

  const onSubmit = async (values) => {
    // console.log('Form submitted with values:', values);
    await ProjectControlStore.setMailingFeedbackConfiguration(values);
  };

  // function that validates a comma-separated list of emails, empty value is accepted
  function validateEmails(value) {
    if (!value) return undefined; // empty value is valid

    const errorMessage = 'Invalid email format. You can input a comma-separated list of emails.';
    const emails = value.split(',').map((email) => email.trim());
    const invalidEmails = emails.filter((email) => emailValidator(email));
    return invalidEmails.length > 0 ? errorMessage : undefined;
  }

  return (
    <Form
      onSubmit={onSubmit}
      keepDirtyOnReinitialize={true}
      initialValues={mailingConfiguration}
      render={({ values, form, handleSubmit, submitting, pristine }) => {
        return (
          <form className='c-fields l-form c-fields--modern' action='#'>
            {/* <h2 className='c-heading-section c-heading-section--large'>General feedback email notification</h2> */}
            <div className='c-field c-field--border l-form__item c-field--modern'>
              <h2 className='c-heading-section c-heading-section--large'>General feedback email notification</h2>
              <div className='c-message c-message--icon is-warning'>
                <SvgIcon icon='edit' />
                <p className='c-message__description'>
                  Activating this option will send an email notification to the user that provided general feedback.
                </p>
                <p className='c-message__description'>
                  You can also specify a list of email addresses to receive these notifications. Those emails will be
                  included in bcc of the notification email (so the original sender won&apos;t see them).
                </p>
              </div>

              <div className='c-field__group'>
                <CheckboxInputField
                  id='checkbox-mailing-enabled'
                  type='checkbox'
                  fieldName='isMailingActive'
                  label='Send email notification on general feedback'
                  isToggle={true}
                  checkedLabel='Enabled'
                  uncheckedLabel='Disabled'
                  hideLabel={false}
                />

                <EngageTextInputField
                  id='input-mailing-list'
                  placeholder='Comma separated list of emails ie. mail1@myco.com,mail2@myco.com'
                  label='Emails to notify (in bcc)'
                  maxLength='100'
                  fieldDescription=''
                  fieldName='emailsToNotify'
                  isRequired={false}
                  fieldClass='l-form__item'
                  tooltipInLabel={{
                    modifier: 'o-tooltip--left-center',
                    icon: 'info-circle',
                    description: `Comma separated list of emails that will receive a notification when a user submits general feedback. This will be included in bcc of the notification email (not visible to the user).`,
                  }}
                  validator={validateEmails}
                />

                <EngageTextareaInputField
                  id='input-mailing-thankyou'
                  placeholder='Thank you message'
                  label='Thank you message'
                  maxLength='400'
                  fieldDescription=''
                  fieldName='thankYouMessage'
                  isRequired={false}
                  fieldClass='l-form__item'
                  tooltipInLabel={{
                    modifier: 'o-tooltip--left-center',
                    icon: 'info-circle',
                    description:
                      'You can customize the THANK YOU MESSAGE that users will see after submitting their feedback. If desired, you can leave this section blank by deleting the default text.',
                  }}
                />

                <EngageTextareaInputField
                  id='input-mailing-noreply'
                  placeholder='No reply message'
                  label='No reply message'
                  maxLength='400'
                  fieldDescription=''
                  fieldName='noReplyMessage'
                  isRequired={false}
                  fieldClass='l-form__item'
                  tooltipInLabel={{
                    modifier: 'o-tooltip--left-center',
                    icon: 'info-circle',
                    description:
                      'You can customize the NO-REPLY MESSAGE that users will see after submitting their feedback. If desired, you can leave this section blank by deleting the default text.',
                  }}
                />

                <SwitchableButtonBar updateAction={handleSubmit} />
              </div>
            </div>
          </form>
        );
      }}
    />
  );
}

MailingFeedbackConfiguration.propTypes = {
  ProjectControlStore: PropTypes.object.isRequired,
  ProjectStore: PropTypes.object.isRequired,
};

export default inject((root) => ({
  ProjectControlStore: root.RootStore.projectControlStore,
  ProjectStore: root.RootStore.projectStore,
}))(observer(MailingFeedbackConfiguration));
